import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Coins, Plus, TrendingUp } from "lucide-react";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { dataCache } from "@/lib/dataCache";
import { useNavigate } from "react-router-dom";

export const WalletCard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [balance, setBalance] = useState(0);
  const [totalSpent, setTotalSpent] = useState(0);

  useEffect(() => {
    if (user) {
      loadWallet();
    }
  }, [user]);

  const loadWallet = async () => {
    if (!user) return;

    // Check cache first
    const cacheKey = `wallet_${user.id}`;
    const cached = dataCache.get<{ balance: number; total_spent: number }>(cacheKey);
    
    if (cached) {
      setBalance(cached.balance || 0);
      setTotalSpent(cached.total_spent || 0);
      return;
    }

    // Fetch from database
    const { data } = await supabase
      .from('wallets')
      .select('balance, total_spent')
      .eq('user_id', user.id)
      .single();

    if (data) {
      setBalance(data.balance || 0);
      setTotalSpent(data.total_spent || 0);
      
      // Cache for 30 seconds
      dataCache.set(cacheKey, data, 30000);
    }
  };

  return (
    <Card className="clean-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Coins className="h-5 w-5 text-primary" />
          StageCoins Wallet
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <div className="flex items-baseline gap-2">
            <span className="text-4xl font-bold text-foreground">{balance}</span>
            <span className="text-sm text-muted-foreground">coins</span>
          </div>
          <Button 
            className="w-full rounded-full" 
            size="sm"
            onClick={() => navigate('/wallet')}
          >
            <Plus className="h-4 w-4 mr-2" />
            Buy More Coins
          </Button>
        </div>
        
        <div className="pt-3 border-t">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Total Spent</span>
            <span className="font-medium flex items-center gap-1">
              <TrendingUp className="h-3 w-3" />
              {totalSpent} coins
            </span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
